const STATIC_INDEXABLE_PATHS = new Set([
  '/', '/listings', '/offers', '/events', '/blog', '/experts', '/about', '/contact',
  '/advertise/spotlight', '/advertise/mailer', '/advertise/mailer/tracking',
  '/advertise/mailer/spotlight-deal', '/privacy-policy', '/terms-of-use', '/cookie-policy',
]);

const DYNAMIC_INDEXABLE_PATHS = [
  /^\/listings\/[^/]+$/, /^\/listings\/[^/]+\/[^/]+$/,
  /^\/offers\/category\/[^/]+$/, /^\/offers\/[^/]+$/,
  /^\/events\/[^/]+$/, /^\/blog\/category\/[^/]+$/, /^\/blog\/[^/]+$/,
  /^\/experts\/[^/]+$/, /^\/community-programs\/[^/]+$/,
  /^\/community-programs\/[^/]+\/category\/[^/]+$/,
];

const NON_INDEXABLE_QUERY_KEYS = new Set([
  'embed', 'editor', 'lockNavigation', 'preview', 'previewTier', 'guideStepKey',
  'guideStepStatus', 'q', 'query', 'search', 'city', 'openOnly', 'tagIds', 'matchMode',
]);

function cleanPathname(pathname = '/') {
  const withoutFragmentOrQuery = String(pathname || '/').split(/[?#]/, 1)[0];
  const withLeadingSlash = withoutFragmentOrQuery.startsWith('/') ? withoutFragmentOrQuery : `/${withoutFragmentOrQuery}`;
  return withLeadingSlash.replace(/\/+$/, '') || '/';
}

export function normalizeCanonicalPath(pathname = '/') {
  const path = cleanPathname(pathname);
  if (path === '/home') return '/';
  if (path === '/directory') return '/listings';
  if (path === '/deals') return '/offers';
  if (path === '/articles') return '/blog';
  if (path === '/pricing') return '/advertise/spotlight';
  if (path.startsWith('/business/')) return `/listings/${path.slice('/business/'.length)}`;
  return path;
}

export function canonicalPathForLocation(pathname = '/', search = '') {
  const path = cleanPathname(pathname);
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  try { if ([...decodeURIComponent(path)].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 || char.charCodeAt(0) === 92)) return ''; } catch { return ''; }
  const singular = new Set(['category', 'page', 'sort', 'view', ...NON_INDEXABLE_QUERY_KEYS]);
  if ([...singular].some(key => params.getAll(key).length > 1 || params.getAll(key).some(value => value.includes('\0'))) || [...params.keys()].some(key => key.includes('[') && singular.has(key.split('[')[0]))) return '';
  if (/^\/listings\/[^/]+(?:\/[^/]+)?\/claim(?:\/|$)/.test(path)) return '';
  if ([...params.keys()].some((key) => NON_INDEXABLE_QUERY_KEYS.has(key))) return '';
  if (params.has('sort') && params.get('sort') !== 'name') return '';
  const indexable = STATIC_INDEXABLE_PATHS.has(path) || DYNAMIC_INDEXABLE_PATHS.some((pattern) => pattern.test(path));
  if (!indexable) return '';
  if (path === '/listings' || path === '/blog' || /^\/blog\/category\/[^/]+$/.test(path) || /^\/community-programs\/[^/]+(?:\/category\/[^/]+)?$/.test(path)) {
    const retained = new URLSearchParams();
    const category = String(params.get('category') || '').trim();
    const pageInput = params.has('page') ? params.get('page') : '1';
    if (!/^[1-9]\d*$/.test(pageInput) || !Number.isSafeInteger(Number(pageInput)) || (!path.startsWith('/community-programs/') && Number(pageInput) > 1000)) return '';
    const page = Number(pageInput);
    if (category && path === '/listings') retained.set('category', category);
    if (Number.isFinite(page) && page > 1) retained.set('page', String(page));
    const query = retained.toString();
    if (query) return `${path}?${query}`;
  }
  return path;
}

export function isPublicCanonicalPath(pathname = '/', search = '') {
  return Boolean(canonicalPathForLocation(pathname, search));
}

export function canonicalOriginForTenant(tenant) {
  const value = String(tenant?.canonicalOrigin || '').trim();
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && !url.port ? url.origin : '';
  } catch {
    return '';
  }
}

export function buildCanonicalUrl(tenant, pathname, fallbackOrigin = '') {
  const origin = canonicalOriginForTenant(tenant);
  if (!origin) return '';
  const raw = String(pathname || '/').split('#', 1)[0];
  if (raw.startsWith('//') || /[\\\r\n]/.test(raw)) return '';
  const queryIndex = raw.indexOf('?');
  const path = normalizeCanonicalPath(queryIndex === -1 ? raw : raw.slice(0, queryIndex));
  const url = new URL(path, `${origin}/`);
  if (url.origin !== origin) return '';
  if (queryIndex !== -1) {
    const input = new URLSearchParams(raw.slice(queryIndex + 1));
    [...new Set(input.keys())].sort().forEach((key) => {
      input.getAll(key).sort().forEach((value) => url.searchParams.append(key, value));
    });
  }
  return url.toString();
}

export function canonicalRedirectUrl(tenant, locationObject = window.location) {
  const canonicalOrigin = canonicalOriginForTenant(tenant);
  if (!canonicalOrigin || canonicalOrigin === locationObject.origin) return '';
  return `${canonicalOrigin}${locationObject.pathname || '/'}${locationObject.search || ''}${locationObject.hash || ''}`;
}

export function removeCanonicalLinks(documentObject = document) {
  const nodes = [...documentObject.head.querySelectorAll('link[rel="canonical"]')];
  nodes.forEach((node) => node.remove());
}

export function ensureSingleCanonicalLink(href, documentObject = document) {
  const nodes = [...documentObject.head.querySelectorAll('link[rel="canonical"]')];
  const canonical = nodes.shift() || documentObject.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = href;
  if (!canonical.parentNode) documentObject.head.appendChild(canonical);
  nodes.forEach((node) => node.remove());
  return canonical;
}
